import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FiCheckCircle,
  FiPercent,
  FiX,
  FiFileText,
} from "react-icons/fi";
import { useAvoirFactureMutation } from "actions/facturation/mutations";
import { formatMontant } from "utils/numberToWordsFr";
import "utils/ConfirmationModal.css";
import "./facturation.css";

if (typeof window !== "undefined") {
  Modal.setAppElement("#root");
}

export default function ModalGenererAvoir({
  show,
  onHide,
  facture,
  onSuccess,
}) {
  const [avoirMode, setAvoirMode] = useState("total"); // 'total' | 'partiel'
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [avoirFacture, { isLoading: isSubmitting }] = useAvoirFactureMutation();

  useEffect(() => {
    if (facture?.items) {
      setItems(
        facture.items.map((item) => ({
          ...item,
          selected: true,
          quantiteAvoir: item.quantite,
          prixUnitaireAvoir: item.prix_unitaire,
        }))
      );
      setNotes(`Avoir sur la facture ${facture.numero}`);
      setAvoirMode("total");
    }
  }, [facture]);

  if (!facture) return null;

  const handleToggleSelect = (index) => {
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, selected: !it.selected } : it))
    );
  };

  const handleQuantityChange = (index, val) => {
    const q = Math.max(0, parseFloat(val) || 0);
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, quantiteAvoir: q } : it))
    );
  };

  const handlePriceChange = (index, val) => {
    const p = Math.max(0, parseInt(val, 10) || 0);
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, prixUnitaireAvoir: p } : it))
    );
  };

  // Calcul en direct pour le mode partiel
  const selectedItems = items.filter((it) => it.selected && it.quantiteAvoir > 0);
  const brutHtPartiel = selectedItems.reduce(
    (sum, it) =>
      sum +
      it.quantiteAvoir *
        it.prixUnitaireAvoir *
        (1 - (it.remise_percent || 0) / 100),
    0
  );
  const netHtPartiel = Math.round(brutHtPartiel);
  const tvaPartiel = facture.apply_tax
    ? Math.round((netHtPartiel * (facture.tva_rate ?? 18)) / 100)
    : 0;
  const totalTtcPartiel = netHtPartiel + tvaPartiel;

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (avoirMode === "total") {
        const res = await avoirFacture({
          id: facture.id,
          notes,
        }).unwrap();
        toast.success(`Avoir total ${res.numero} émis avec succès.`);
      } else {
        if (selectedItems.length === 0) {
          toast.error("Veuillez sélectionner au moins une ligne pour l'avoir partiel.");
          return;
        }
        const payloadItems = selectedItems.map((it) => ({
          description: it.description,
          quantite: it.quantiteAvoir,
          prix_unitaire: it.prixUnitaireAvoir,
          remise_percent: it.remise_percent || 0,
          code_comptable: it.code_comptable,
        }));

        const res = await avoirFacture({
          id: facture.id,
          items: payloadItems,
          notes,
        }).unwrap();
        toast.success(
          `Avoir partiel ${res.numero} (${formatMontant(res.total_ttc)}) émis avec succès.`
        );
      }

      onHide();
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(
        err?.data?.message || "Erreur lors de la génération de l'avoir."
      );
    }
  };

  return (
    <Modal
      isOpen={show}
      onRequestClose={onHide}
      contentLabel="Émettre un avoir"
      className="Modal avoir-modal"
      overlayClassName="Overlay"
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e2e8f0",
          paddingBottom: "16px",
          marginBottom: "20px",
          width: "100%",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              background: "#fee2e2",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiFileText size={22} />
          </div>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: "#0f172a" }}>
              Émission d&apos;un Avoir : {facture.numero}
            </h3>
            <p style={{ fontSize: 12, color: "#64748b", margin: "2px 0 0 0" }}>
              Client : <strong>{facture.client_nom}</strong> • Montant Facturé : <strong>{formatMontant(facture.total_ttc)} FCFA</strong>
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onHide}
          style={{
            background: "#f1f5f9",
            border: "none",
            borderRadius: "50%",
            width: 34,
            height: 34,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#64748b",
            cursor: "pointer",
          }}
        >
          <FiX size={18} />
        </button>
      </div>

      <Form onSubmit={handleSubmit} style={{ width: "100%" }}>
        {/* Choix du type d'avoir */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", display: "block", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            1. Mode d&apos;émission de l&apos;avoir
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div
              onClick={() => setAvoirMode("total")}
              style={{
                border: `2px solid ${avoirMode === "total" ? "#009688" : "#e2e8f0"}`,
                background: avoirMode === "total" ? "#e0f7fa" : "#f8fafc",
                borderRadius: 12,
                padding: 14,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, color: "#0f172a", fontSize: 14 }}>
                <FiCheckCircle color={avoirMode === "total" ? "#009688" : "#94a3b8"} size={18} />
                <span>Avoir Total (100%)</span>
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, lineHeight: 1.4 }}>
                Annule la totalité de la facture ({formatMontant(facture.total_ttc)} FCFA) et contre-passe les écritures.
              </div>
            </div>

            <div
              onClick={() => setAvoirMode("partiel")}
              style={{
                border: `2px solid ${avoirMode === "partiel" ? "#009688" : "#e2e8f0"}`,
                background: avoirMode === "partiel" ? "#e0f7fa" : "#f8fafc",
                borderRadius: 12,
                padding: 14,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, color: "#0f172a", fontSize: 14 }}>
                <FiPercent color={avoirMode === "partiel" ? "#009688" : "#94a3b8"} size={18} />
                <span>Avoir Partiel (Sur mesure)</span>
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, lineHeight: 1.4 }}>
                Sélectionnez les articles ou prestations avec quantités/prix ajustables.
              </div>
            </div>
          </div>
        </div>

        {/* Tableau des lignes en mode Partiel */}
        {avoirMode === "partiel" && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8, display: "block" }}>
              2. Lignes &amp; Quantités à créditer
            </label>
            <div style={{ border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden" }}>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f1f5f9", borderBottom: "1px solid #cbd5e1", color: "#475569", textAlign: "left" }}>
                    <th style={{ padding: "8px 12px", width: 40, textAlign: "center" }}>#</th>
                    <th style={{ padding: "8px 12px" }}>Description</th>
                    <th style={{ padding: "8px 12px", width: 120 }}>Quantité Avoir</th>
                    <th style={{ padding: "8px 12px", width: 150 }}>Prix Unitaire HT</th>
                    <th style={{ padding: "8px 12px", textAlign: "right", width: 140 }}>Total Ligne HT</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const lineHt = Math.round(
                      it.quantiteAvoir *
                        it.prixUnitaireAvoir *
                        (1 - (it.remise_percent || 0) / 100)
                    );
                    return (
                      <tr
                        key={it.id || idx}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          background: it.selected ? "#ffffff" : "#f8fafc",
                          opacity: it.selected ? 1 : 0.5,
                        }}
                      >
                        <td style={{ padding: "8px 12px", textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={it.selected}
                            onChange={() => handleToggleSelect(idx)}
                            style={{ width: 16, height: 16, cursor: "pointer" }}
                          />
                        </td>
                        <td style={{ padding: "8px 12px", fontWeight: 600, color: "#0f172a" }}>
                          {it.description}
                        </td>
                        <td style={{ padding: "8px 12px" }}>
                          <input
                            type="number"
                            step="any"
                            min="0.01"
                            max={it.quantite}
                            disabled={!it.selected}
                            value={it.quantiteAvoir}
                            onChange={(e) => handleQuantityChange(idx, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "5px 8px",
                              border: "1px solid #cbd5e1",
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          />
                        </td>
                        <td style={{ padding: "8px 12px" }}>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            disabled={!it.selected}
                            value={it.prixUnitaireAvoir}
                            onChange={(e) => handlePriceChange(idx, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "5px 8px",
                              border: "1px solid #cbd5e1",
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          />
                        </td>
                        <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: "#009688", fontSize: 13 }}>
                          {it.selected ? formatMontant(lineHt) : ":"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Synthèse Financière de l'Avoir Partiel */}
            <div
              style={{
                background: "#f0fdfa",
                border: "1px solid #99f6e4",
                borderRadius: 10,
                padding: "12px 18px",
                marginTop: 12,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", gap: 20 }}>
                <div>
                  <div style={{ fontSize: 11, color: "#009688", fontWeight: 600 }}>MONTANT NET HT</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#115e59" }}>{formatMontant(netHtPartiel)} FCFA</div>
                </div>
                {facture.apply_tax && (
                  <div>
                    <div style={{ fontSize: 11, color: "#009688", fontWeight: 600 }}>TVA ({facture.tva_rate ?? 18}%)</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#115e59" }}>{formatMontant(tvaPartiel)} FCFA</div>
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>TOTAL AVOIR TTC</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#009688" }}>
                  {formatMontant(totalTtcPartiel)} FCFA
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Motif / Notes de l'avoir */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6, display: "block" }}>
            3. Motif / Notes de l&apos;avoir
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ex: Retour de marchandise, geste commercial, rectification..."
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              fontSize: 13,
            }}
          />
        </div>

        {/* Actions Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
          <button
            type="button"
            onClick={onHide}
            disabled={isSubmitting}
            style={{
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              padding: "8px 18px",
              borderRadius: 31,
              fontSize: 13,
              fontWeight: 600,
              color: "#475569",
              cursor: "pointer",
            }}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting || (avoirMode === "partiel" && totalTtcPartiel <= 0)}
            className="d-inline-flex align-items-center"
            style={{
              background: "#009688",
              color: "#ffffff",
              border: "none",
              padding: "8px 22px",
              borderRadius: 31,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {isSubmitting && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
            )}
            {isSubmitting ? "Émission en cours..." : "Confirmer & Émettre l'Avoir"}
          </button>

        </div>
      </Form>
    </Modal>
  );
}
